import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Generic function to seed any auction house
export const seedAuctionHouse = async (config: {
  id: string;
  name: string;
  subdomain: string;
  contactEmail: string;
  phone?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  website?: string;
  description: string;
  tagline?: string;
  primaryColor?: string;
  secondaryColor?: string;
}) => {
  try {
    console.log(`🌱 Seeding ${config.name}...`);
    
    const auctionHousesRef = collection(db, 'auctionHouses');
    const houseRef = doc(auctionHousesRef, config.id);
    
    await setDoc(houseRef, {
      name: config.name,
      subdomain: config.subdomain,
      slug: config.id,
      
      contactEmail: config.contactEmail,
      phone: config.phone || '',
      address: config.address || null,
      website: config.website || '',
      
      logoUrl: '',
      bannerUrl: '',
      primaryColor: config.primaryColor || '#1e40af',
      secondaryColor: config.secondaryColor || '#7c3aed',
      description: config.description,
      tagline: config.tagline || 'Predict. Wager. Win.',
      
      tokenCostPerUser: 2.00,
      commissionRate: 0.15,
      monthlyFee: 0,
      revenueShareModel: 'hybrid',
      
      subscriptionTiers: {
        free: {
          enabled: true,
          ringerAllotment: 1000,
          creditAllotment: 10,
        },
        basic: {
          enabled: true,
          price: 499,
          ringerAllotment: 2500,
          creditAllotment: 50,
        },
        premium: {
          enabled: true,
          price: 999,
          ringerAllotment: 5000,
          creditAllotment: 100,
        },
      },
      
      isActive: true,
      acceptsLeftBids: true,
      allowCrossPlatformPredictions: true,
      redemptionStoreEnabled: true,
      
      totalAuctions: 0,
      totalLots: 0,
      totalPredictions: 0,
      totalUsers: 0,
      
      customDomain: `${config.subdomain}.auctionringer.com`,
      customDomainVerified: false,
      
      joinedDate: Timestamp.now(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      
      ownerId: '',
      adminUserIds: [],
    });
    
    console.log(`✅ ${config.name} seeded successfully!`);
    return true;
  } catch (error) {
    console.error(`❌ Error seeding ${config.name}:`, error);
    return false;
  }
};

// Schmidt Auctions
export const seedSchmidtAuctions = async () => {
  return seedAuctionHouse({
    id: 'schmidt-auctions',
    name: 'Schmidt Auctions',
    subdomain: 'schmidt',
    contactEmail: 'info@schmidtauctions.com',
    phone: '(610) 323-1573',
    address: {
      street: '1320 Industrial Boulevard',
      city: 'Southampton',
      state: 'PA',
      zip: '18966',
      country: 'USA',
    },
    website: 'https://schmidtauctions.com',
    description: 'Family-owned auction house specializing in sports cards, memorabilia, and collectibles.',
    tagline: 'Where Collectors Come to Win',
    primaryColor: '#1e40af',
    secondaryColor: '#7c3aed',
  });
};

// Example: Heritage Auctions
export const seedHeritageAuctions = async () => {
  return seedAuctionHouse({
    id: 'heritage-auctions',
    name: 'Heritage Auctions',
    subdomain: 'heritage',
    contactEmail: 'info@ha.com',
    phone: '(214) 528-3500',
    address: {
      street: '2801 W Airport Freeway',
      city: 'Dallas',
      state: 'TX',
      zip: '75261',
      country: 'USA',
    },
    website: 'https://www.ha.com',
    description: 'World\'s largest collectibles auctioneer. Sports, coins, comics, art, and more.',
    tagline: 'The World\'s Largest Collectibles Auctioneer',
    primaryColor: '#8B0000',
    secondaryColor: '#FFD700',
  });
};

// Example: Goldin Auctions
export const seedGoldinAuctions = async () => {
  return seedAuctionHouse({
    id: 'goldin-auctions',
    name: 'Goldin Auctions',
    subdomain: 'goldin',
    contactEmail: 'info@goldinauctions.com',
    website: 'https://goldinauctions.com',
    description: 'Premium sports cards, memorabilia, and collectibles auction house.',
    tagline: 'Where Champions Are Made',
    primaryColor: '#000000',
    secondaryColor: '#FFD700',
  });
};

// Make them available in browser console
if (typeof window !== 'undefined') {
  (window as any).seedSchmidt = seedSchmidtAuctions;
  (window as any).seedHeritage = seedHeritageAuctions;
  (window as any).seedGoldin = seedGoldinAuctions;
  (window as any).seedAuctionHouse = seedAuctionHouse;
}